import * as React from 'react';
import {
  capitalize,
  filter, isEmpty, isNull, isString,
} from 'lodash';
import { format } from 'date-fns';
import { SearchOutlined } from '@ant-design/icons';
import { ChevronLeftIcon, ChevronRightIcon, ArrowDownToLineIcon } from '@revfluence/fresh-icons/regular/esm';
import { DataTable } from '@frontend/app/refresh-components/DataTableV2/Table';
import { SortingState } from '@tanstack/react-table';
import { IEnabledOfferSources } from '@affiliates/types';
import { GetAllPrograms_programs } from '@frontend/app/queries/types/GetAllPrograms';
import { IDateRangeSettings } from '@frontend/app/components';
import { TableColumnsIcon } from '@revfluence/fresh-icons/solid/esm';
import { ChevronDownIcon, ChevronUpIcon, ArrowDownArrowUpIcon } from '@revfluence/fresh-icons/regular/esm';
import Tag from '@frontend/app/refresh-components/Tag';
import { XmarkIcon } from '@revfluence/fresh-icons/regular';
import { AddOfferButton } from './AddOfferButton';
import { RefreshGetStarted } from './RefreshGetStarted';
import {
  IDashboardOffer,
  IOfferTableCsvRow,
  OFFER_STATUS,
  TOnClickAddOffer,
} from './types';

import { DataFormat, formatValue } from '../../utils';
import { OFFER_STATUS_TYPE, STATS_OFFER_TYPE } from '../../types/globalTypes';
import { Button } from '@/shadcn/components/ui/button';
import { Input } from '@/shadcn/components/ui/input';
import { Card } from '@/shadcn/components/ui/card';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { Popover, PopoverTrigger, PopoverContent } from '@/shadcn/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
import { RefreshOfferNameCell } from './RefreshOfferNameCell';
import { IHeader } from '../../hooks';
import { dummyOfferTableColumns } from '../../pages/Dashboard/RefreshDashboardPage';

interface IProps {
  data: readonly IDashboardOffer[];
  missingShopifyCredentials: boolean;
  onClickAddOffer: TOnClickAddOffer;
  onClickExport: (columns: IHeader[], data: IOfferTableCsvRow[]) => void;
  onOfferClicked(offerId: number): void;
  sources: IEnabledOfferSources;
  programs: GetAllPrograms_programs[];
  baseUri: string;
  clientName: string;
  dateRangeSettings: IDateRangeSettings;
  selectedOfferType: STATS_OFFER_TYPE;
  setSelectedOfferType: (value: STATS_OFFER_TYPE) => void;
  selectedFilters: OFFER_STATUS_TYPE[];
  setSelectedFilters: (value: OFFER_STATUS_TYPE[]) => void;
  selectedPrograms: number[];
}

const asOfferStatus = (status: string, expired: boolean, archived: boolean) => {
  const isActive = status === OFFER_STATUS.ACTIVE;
  const isPaused = status === OFFER_STATUS.PAUSED;
  const isDeleted = status === OFFER_STATUS.DELETED;
  const isExpired = expired;
  if (archived) {
    return OFFER_STATUS.ARCHIVED;
  } if (isExpired) {
    return OFFER_STATUS.EXPIRED;
  } if (isActive) {
    return OFFER_STATUS.ACTIVE;
  } if (isPaused) {
    return OFFER_STATUS.PAUSED;
  } if (isDeleted) {
    return OFFER_STATUS.DELETED;
  }
  throw new Error(`Invalid offer status: "${status}".`);
};

const getTagColor = (status: string) => {
  if (status === 'ARCHIVED') {
    return 'bg-grey-2';
  }
  if (status === 'PAUSED') {
    return 'bg-yellow-2';
  }
  if (status === 'EXPIRED') {
    return 'bg-red-2';
  }
  if (status === 'ACTIVE') {
    return 'bg-green-2';
  }
  return 'bg-grey-2';
};

export const RefreshOfferTable: React.FC<Readonly<IProps>> = (props) => {
  const {
    data,
    missingShopifyCredentials,
    onClickAddOffer,
    onClickExport,
    onOfferClicked,
    sources,
    programs,
    selectedOfferType,
    selectedFilters = [],
    selectedPrograms = [],
  } = props;

  const [searchText, setSearchText] = React.useState('');
  const [page, setPage] = React.useState(1);
  const pageSize = 100;
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<Record<string, boolean>>({});

  const mapProgramById = React.useCallback(
    (id: number): string => {
      const program = programs?.find((program) => program.id === id);
      return program ? program?.title : '-';
    },
    [programs],
  );
  const columns = React.useMemo(() => [
    {
      id: 'offerName',
      HeaderName: 'Offer Name',
      accessorKey: 'offerName',
      header: ({ column }) => <SortButton column={column}>Name</SortButton>,
      cell: ({ row }) => (
        <RefreshOfferNameCell
          imageUrl={row.original.offerImageUrl}
          name={row.original.offerName}
          source={row.original.source}
          iconName={row.original.icon}
        />
      ),
      sortable: true,
      size: 250,
      minSize: 250,
      maxSize: 250,
    },
    {
      id: 'programId',
      HeaderName: 'Connected Project',
      accessorKey: 'programId',
      header: ({ column }) => <SortButton column={column}>Connected Project</SortButton>,
      cell: ({ row }) => {
        const project = mapProgramById(row.original.programId);
        const truncated = project.length > 20 ? `${project.substring(0, 20)}...` : project;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="truncate max-w-[160px] inline-block align-middle cursor-default">{truncated}</span>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
              <span className="break-all">{project}</span>
            </TooltipContent>
          </Tooltip>
        );
      },
      sortable: true,
      size: 200,
      minSize: 200,
      maxSize: 200,
    },
    {
      id: 'url',
      HeaderName: 'URL ',
      accessorKey: 'url',
      header: ({ column }) => <SortButton column={column}>URL</SortButton>,
      cell: ({ row }) => {
        const url = row.original.url;
        if (!url) return '-';

        const truncatedUrl = url.length > 25 ? `${url.substring(0, 25)}...` : url;

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline whitespace-nowrap text-primary hover:text-primary">
                {truncatedUrl}
              </a>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-[300px] break-all">{url}</p>
            </TooltipContent>
          </Tooltip>
        );
      },
      size: 250,
      minSize: 250,
      maxSize: 250,
      sortable: true,
    },
    {
      id: 'status',
      HeaderName: 'Status',
      accessorKey: 'status',
      header: ({ column }) => <SortButton column={column}>Status</SortButton>,
      cell: ({ row }) => <Tag label={capitalize(row.original.status)} bgColor={getTagColor(row.original.status)} textColor="text-grey-6" />,
      sortable: true,
      size: 120,
      minSize: 120,
      maxSize: 120,
    },
    {
      id: 'members',
      HeaderName: 'Members',
      accessorKey: 'members',
      header: ({ column }) => <SortButton column={column}>Members</SortButton>,
      cell: ({ row }) => <div className="text-right w-full">{formatValue(DataFormat.Integer, row.original.members)}</div>,
      meta: { align: 'right' },
      size: 120,
      minSize: 120,
      maxSize: 120,
      sortable: true,
    },
    {
      id: 'clicks',
      HeaderName: 'Clicks',
      accessorKey: 'clicks',
      header: ({ column }) => <SortButton column={column}>Clicks</SortButton>,
      cell: ({ row }) => <div className="text-right w-full">{formatValue(DataFormat.Integer, row.original.clicks)}</div>,
      meta: { align: 'right' },
      size: 100,
      minSize: 100,
      maxSize: 100,
      sortable: true,
    },
    {
      id: 'conversions',
      HeaderName: 'Conversions',
      accessorKey: 'conversions',
      header: ({ column }) => <SortButton column={column}>Conversions</SortButton>,
      cell: ({ row }) => <div className="text-right w-full">{formatValue(DataFormat.Integer, row.original.conversions)}</div>,
      meta: { align: 'right' },
      size: 150,
      minSize: 150,
      maxSize: 150,
      sortable: true,
    },
    {
      id: 'salesAmount',
      HeaderName: 'Sales Amt',
      accessorKey: 'salesAmount',
      header: ({ column }) => <SortButton column={column}>Sales Amt</SortButton>,
      cell: ({ row }) => <div className="text-right w-full">${formatValue(DataFormat.Money, row.original.salesAmount)}</div>,
      meta: { align: 'right' },
      size: 120,
      minSize: 120,
      maxSize: 120,
      sortable: true,
    },
    {
      id: 'payoutEarned',
      HeaderName: 'Commissions Earned',
      accessorKey: 'payoutEarned',
      header: ({ column }) => <SortButton column={column}>Commissions Earned</SortButton>,
      cell: ({ row }) => <div className="text-right w-full">${formatValue(DataFormat.Money, row.original.payoutEarned)}</div>,
      meta: { align: 'right' },
      size: 200,
      minSize: 200,
      maxSize: 200,
      sortable: true,
    },
    {
      id: 'payoutMade',
      HeaderName: 'Payout',
      accessorKey: 'payoutMade',
      header: ({ column }) => <SortButton column={column}>Payout</SortButton>,
      cell: ({ row }) => <div className="text-right w-full">${formatValue(DataFormat.Money, row.original.payoutMade)}</div>,
      meta: { align: 'right' },
      size: 120,
      minSize: 120,
      maxSize: 120,
      sortable: true,
    },
    {
      id: 'avgSale',
      HeaderName: 'Avg. Sale',
      accessorKey: 'avgSale',
      header: ({ column }) => <SortButton column={column}>Avg. Sale</SortButton>,
      cell: ({ row }) => <div className="text-right w-full">${formatValue(DataFormat.Money, row.original.avgSale)}</div>,
      meta: { align: 'right' },
      size: 150,
      minSize: 150,
      maxSize: 150,
      sortable: true,
    },
    {
      id: 'createdDate',
      HeaderName: 'Created On',
      accessorKey: 'createdDate',
      header: ({ column }) => <SortButton column={column}>Created On</SortButton>,
      cell: ({ row }) => (
        <span className="whitespace-nowrap">
          {format(new Date(row.original.createdDate), 'MM/dd/yyyy, hh:mm')}
        </span>
      ),
      size: 150,
      minSize: 150,
      maxSize: 150,
      sortable: true,
    },
  ], [mapProgramById]);

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const SortButton = ({ column, children }: { column: any; children: React.ReactNode }) => {
    const isSorted = column.getIsSorted();
    const [isHovering, setIsHovering] = React.useState(false);

    const handleSort = () => {
      if (isSorted === 'asc') {
        column.toggleSorting(true);
      } else if (isSorted === 'desc') {
        column.clearSorting();
      } else {
        column.toggleSorting(false);
      }
    };

    return (
      <Button
        variant="ghost"
        className="px-2 hover:bg-transparent w-full"
        onClick={handleSort}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div className="flex w-full justify-between items-center">
          <span>{children}</span>
          <span className="h-3 w-3 flex items-center justify-center">
            {isSorted === 'asc' && <ChevronUpIcon className="h-3 w-3" />}
            {isSorted === 'desc' && <ChevronDownIcon className="h-3 w-3" />}
            {!isSorted && isHovering && <ArrowDownArrowUpIcon className="h-3 w-3" />}
          </span>
        </div>
      </Button>
    );
  };

  React.useEffect(() => {
    const initialVisibility = columns.reduce((acc, column) => {
      acc[column.id] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setColumnVisibility(initialVisibility);
  }, [columns]);

  const filteredData = React.useMemo(() => {
    return data.map((item) => ({
      ...item,
      status: asOfferStatus(item.status, item.expired, !isNull(item.archivedDate))
    })).filter((item) => {
      const isProjectMatched =
        !selectedPrograms.length || selectedPrograms.includes(item.programId);
      const isOfferTypeMatched = selectedOfferType === 'ALL_OFFERS'
        || (selectedOfferType === 'LINK_OFFER' && item.offerType === 'LINK')
        || (selectedOfferType === 'PROMO_OFFER' && item.offerType === 'PROMO_CODE');
      const isStatusMatched = selectedFilters.length > 0
        && selectedFilters.some((status) => status.toUpperCase() === item.status);
      const isSearchTextMatched = searchText.trim() === ''
        || (isString(item.offerName) && item.offerName.toLowerCase().includes(searchText.toLowerCase()));
      return isProjectMatched && isOfferTypeMatched && isStatusMatched && isSearchTextMatched;
    });
  }, [data, selectedOfferType, selectedFilters, searchText, selectedPrograms]);

  // Reset page to 1 if filteredData shrinks below current page
  React.useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    if (page > totalPages) setPage(1);
  }, [filteredData.length, page, pageSize]);

  const sortedData = React.useMemo(() => {
    if (!sorting.length) return filteredData;
    const [{ id, desc }] = sorting;
    return [...filteredData].sort((a, b) => {
      const aValue = a[id as keyof typeof a];
      const bValue = b[id as keyof typeof b];
      if (aValue === bValue) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return desc ? bValue - aValue : aValue - bValue;
      }
      return desc
        ? String(bValue).localeCompare(String(aValue))
        : String(aValue).localeCompare(String(bValue));
    });
  }, [filteredData, sorting]);

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize]);

  const handleClickExport = React.useCallback(() => {
    const csvColumnConfig = [
      { headerName: 'No.', field: 'number' },
      ...columns.map((col) => ({
        headerName: col.HeaderName,
        field: col.accessorKey,
      })),
    ];
    const csvData = filter(filteredData, (row) => row.status !== OFFER_STATUS.DELETED)
      .map((row, idx) => ({
        number: idx + 1,
        ...row,
        offerName: row.offerName,
        createdDate: format(new Date(row.createdDate), 'MM/dd/yyyy, hh:mm'),
        programId: mapProgramById(row.programId),
      }));
    if (!isEmpty(csvData)) {
      onClickExport(csvColumnConfig, csvData);
    }
  }, [columns, onClickExport, filteredData, mapProgramById]);

  if (isEmpty(data)) {
    return <RefreshGetStarted onClickCreate={onClickAddOffer} sources={sources} />;
  }

  return (
    <Card className="h-[65vh] overflow-y-auto flex flex-col">
      <div className="flex items-center justify-between py-4 px-6">
        <div>
          <span className="mr-2">{`${filteredData.length} Offers`}</span>
          <AddOfferButton
            disabled={false}
            missingShopifyCredentials={missingShopifyCredentials}
            onClick={onClickAddOffer}
            sources={sources}
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex items-center">
            <Button
              variant="ghost"
              className="w-8 h-8 flex items-center justify-center z-20"
              size="icon"
              onClick={() => setIsSearchOpen((v) => !v)}
              aria-label="Search"
            >
              {isSearchOpen ? (
                <XmarkIcon className="text-muted-foreground" />
              ) : (
                <SearchOutlined className="text-muted-foreground" />
              )}
            </Button>
            <div
              className={`absolute right-10 top-0 transition-all duration-300 ${isSearchOpen ? 'opacity-100 w-48' : 'opacity-0 w-0'} overflow-hidden`}
              style={{ zIndex: 10 }}
            >
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                type="search"
                placeholder="Search Offer"
                className="rounded-lg bg-grey-0 pl-8 w-48 focus:ring-0 focus-visible:ring-0 outline-none"
                autoFocus={isSearchOpen}
              />
              <SearchOutlined className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            </div>
          </div>
          <Button
            disabled={isEmpty(data)}
            onClick={handleClickExport}
            variant="ghost"
            size="icon"
            aria-label="Export offers"
          >
            <ArrowDownToLineIcon />
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={isEmpty(data)}
                aria-label="Toggle column visibility"
              >
                <TableColumnsIcon />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-4">
              <div className="flex flex-col gap-2">
                <h4 className="font-medium mb-2">Visible Columns</h4>
                {columns.map((column) => (
                  <label key={column.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={columnVisibility[column.id]}
                      disabled={column.id === 'offerName'}
                      onCheckedChange={(checked) => {
                        setColumnVisibility((prev) => ({
                          ...prev,
                          [column.id]: checked === true,
                        }));
                      }}
                    />
                    <span>{column.HeaderName}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <div className="flex gap-2 items-center ml-4">
            <span>{`${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, filteredData.length)} of ${filteredData.length}`}</span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                className="w-8 h-8"
                size="icon"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeftIcon className="text-gray-500" fontSize={16} />
              </Button>
              <Button
                variant="ghost"
                className="w-8 h-8"
                size="icon"
                disabled={page === totalPages || filteredData.length === 0}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next page"
              >
                <ChevronRightIcon className="text-gray-500" fontSize={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-2 overflow-y-auto border-t">
        {paginatedData.length > 0 ? (
          <DataTable
            columns={columns}
            data={paginatedData}
            enableColumnResizing
            getRowId={(row) => row.offerId.toString()}
            onRowClick={(row) => onOfferClicked(row.offerId)}
            className="min-w-[900px] [&_td]:py-1 [&_td]:h-10! [&_td]:max-h-10! [&_td]:align-middle"
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onSortingChange={setSorting}
            pinnedLeft={['offerName']}
          />
        ) : (
          <DataTable
            columns={dummyOfferTableColumns}
            data={[]}
          />
        )}
      </div>
    </Card>
  );
};
